import * as Popover from "@radix-ui/react-popover";
import { CheckIcon, ChevronDownIcon, MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/utils/cn";
import type { SelectOption } from "./Select";

export interface SearchableSelectProps {
  name: string;
  value?: string;
  onValueChange: (value: string) => void;
  onOpenChange?: (open: boolean) => void;
  placeholder?: string;
  options: SelectOption[];
  className?: string;
  triggerHeightClass?: string;
  /** Portal target — pass the dialog content element inside a Radix modal. */
  container?: HTMLElement | null;
  searchPlaceholder?: string;
}

/**
 * A single-select with a search box — for master-data lists too long to scroll
 * (Current Role Level has ~90 entries, Primary Operator Function ~20).
 *
 * Reached through `<SelectRoot searchable />`, so a call site opts in with one
 * prop and keeps its value/onValueChange wiring. Built on Radix Popover rather
 * than Radix Select: Select owns the keyboard for its type-ahead and steals
 * focus back from any input placed inside it.
 *
 * Keyboard: type to filter, ↑/↓ to move, Enter to pick, Esc to close. The
 * trigger is a real button, so react-hook-form refs and scrollToFirstError can
 * focus it like any other field.
 */
export const SearchableSelect = forwardRef<HTMLButtonElement, SearchableSelectProps>(
  (
    {
      name,
      value,
      onValueChange,
      onOpenChange,
      placeholder = "",
      options,
      className = "",
      triggerHeightClass = "h-[54px]",
      container,
      searchPlaceholder = "Search…",
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const listRef = useRef<HTMLDivElement>(null);

    const selected = options.find((o) => o.value === value);
    const filtered = useMemo(() => {
      const q = query.trim().toLowerCase();
      return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
    }, [options, query]);

    const setOpenState = (next: boolean) => {
      setOpen(next);
      onOpenChange?.(next);
      if (next) {
        setQuery("");
        // Start on the current value so Enter keeps it and ↓ moves from it.
        const idx = options.findIndex((o) => o.value === value);
        setActive(idx >= 0 ? idx : 0);
      }
    };

    // Keep the highlighted row in range as the filter narrows, and in view.
    useEffect(() => {
      if (active > filtered.length - 1) setActive(Math.max(0, filtered.length - 1));
    }, [filtered.length, active]);
    useEffect(() => {
      if (!open) return;
      // One frame later: on open the portalled list has not mounted yet, and
      // the current value would sit out of view below the fold.
      const frame = requestAnimationFrame(() =>
        listRef.current
          ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
          ?.scrollIntoView({ block: "nearest" }),
      );
      return () => cancelAnimationFrame(frame);
    }, [active, open]);

    const pick = (option: SelectOption | undefined) => {
      if (!option || option.disabled) return;
      onValueChange(option.value);
      setOpenState(false);
    };

    return (
      <Popover.Root open={open} onOpenChange={setOpenState}>
        <Popover.Trigger asChild>
          <button
            ref={ref}
            type="button"
            name={name}
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            className={cn(
              "flex w-full min-w-0 items-center justify-between gap-2 rounded-[14px] border border-theme-border bg-[var(--theme-input-bg)] px-3 text-left text-16 text-theme-text transition-colors",
              "focus:border-primary-350 focus:outline-none focus:ring-1 focus:ring-primary-350",
              open && "border-primary-350 ring-1 ring-primary-350",
              triggerHeightClass,
              className,
            )}
          >
            <span
              className={cn(
                "min-w-0 truncate",
                selected ? "text-theme-text" : "text-[var(--theme-input-placeholder)]",
              )}
            >
              {selected?.label ?? placeholder}
            </span>
            <ChevronDownIcon
              width={16}
              height={16}
              aria-hidden
              className={cn(
                "flex-shrink-0 text-theme-text-secondary transition-transform",
                open && "rotate-180",
              )}
            />
          </button>
        </Popover.Trigger>

        <Popover.Portal container={container ?? undefined}>
          <Popover.Content
            align="start"
            sideOffset={6}
            collisionPadding={12}
            // Search box gets focus on open, not the first row.
            onOpenAutoFocus={(e) => e.preventDefault()}
            className="z-[70] flex w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-24px)] flex-col overflow-hidden rounded-[14px] border border-theme-border bg-theme-bg-surface shadow-[0px_20px_25px_0px_rgba(0,0,0,0.1),0px_8px_10px_0px_rgba(0,0,0,0.1)] outline-none"
          >
            <div className="flex items-center gap-2 border-b border-theme-border-subtle px-3">
              <MagnifyingGlassIcon className="h-4 w-4 flex-shrink-0 text-theme-text-muted" aria-hidden />
              <input
                autoFocus
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(0);
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setActive((i) => Math.min(i + 1, filtered.length - 1));
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setActive((i) => Math.max(i - 1, 0));
                  } else if (e.key === "Enter") {
                    e.preventDefault();
                    pick(filtered[active]);
                  }
                }}
                placeholder={searchPlaceholder}
                aria-label={`Search ${placeholder || name}`}
                aria-controls={`${name}-listbox`}
                className="h-10 w-full min-w-0 bg-transparent text-14 text-theme-text placeholder:text-[var(--theme-input-placeholder)] focus:outline-none"
              />
            </div>
            <div
              ref={listRef}
              id={`${name}-listbox`}
              role="listbox"
              className="max-h-[260px] overflow-y-auto py-1"
            >
              {filtered.length === 0 ? (
                <p className="px-3 py-3 text-14 text-theme-text-muted">No matches for “{query.trim()}”</p>
              ) : (
                filtered.map((option, i) => {
                  const isSelected = option.value === value;
                  return (
                    <div
                      key={option.value}
                      data-index={i}
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={option.disabled || undefined}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => pick(option)}
                      className={cn(
                        "flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-14",
                        i === active && "bg-theme-accent-subtle",
                        isSelected ? "font-semibold text-theme-accent" : "text-theme-text",
                        option.disabled && "cursor-not-allowed opacity-50",
                      )}
                    >
                      <span className="min-w-0 truncate">{option.label}</span>
                      {isSelected && <CheckIcon className="h-4 w-4 flex-shrink-0" aria-hidden />}
                    </div>
                  );
                })
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    );
  },
);

SearchableSelect.displayName = "SearchableSelect";
